import type { Message, MessagePart, RoomSummary } from "./types.js";

/** Extract plain text from message parts (text parts joined with newlines). */
export function textFromMessageParts(parts: MessagePart[]): string {
  return parts
    .filter((p) => p.type === "text" && typeof p.text === "string")
    .map((p) => p.text)
    .join("\n");
}

export type HasMessageId = {
  id: string;
  createdAt?: string | Date | null;
};

/** Merge messages by id, sorted oldest-first by `createdAt`. */
export function mergeMessagesById<T extends HasMessageId>(
  existing: readonly T[],
  incoming: readonly T[],
): T[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const msg of incoming) {
    byId.set(msg.id, msg);
  }
  return [...byId.values()].sort((a, b) => messageTime(a) - messageTime(b));
}

function messageTime(message: HasMessageId): number {
  if (!message.createdAt) return 0;
  const t = new Date(message.createdAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

export type ReconcileSelfSentOptions<T> = {
  /** Drop in-flight optimistic sends from this author (default: role/status check for UI libs). */
  isOptimisticSend?: (message: T, serverMessage: T) => boolean;
};

/**
 * Replace the in-flight optimistic send with the server message.
 * Default heuristic: same author, status `sending` (MUI x-chat optimistic rows).
 */
export function reconcileSelfSentMessage<T extends HasMessageId>(
  existing: readonly T[],
  serverMessage: T,
  options: ReconcileSelfSentOptions<T> = {},
): T[] {
  const isOptimistic =
    options.isOptimisticSend ??
    ((message, server) => {
      const m = message as { role?: string; author?: { id?: string }; status?: string };
      const s = server as { author?: { id?: string } };
      if (message.id === server.id) return true;
      if (m.role !== "user" || m.author?.id !== s.author?.id) return false;
      return m.status === "sending";
    });
  const filtered = existing.filter((message) => !isOptimistic(message, serverMessage));
  return mergeMessagesById(filtered, [serverMessage]);
}

function roomActivityTime(room: RoomSummary): number {
  if (!room.lastMessage?.createdAt) return 0;
  const t = new Date(room.lastMessage.createdAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Sort rooms by most recent message activity (matches gateway room list order). */
export function sortRoomsByLatestActivity(rooms: readonly RoomSummary[]): RoomSummary[] {
  return [...rooms].sort((a, b) => {
    const diff = roomActivityTime(b) - roomActivityTime(a);
    if (diff !== 0) return diff;
    return a.id.localeCompare(b.id);
  });
}

/** Whether a peer has read a message based on `peerLastReadAt`. */
export function isMessageReadByPeer(
  message: Pick<Message, "createdAt" | "senderId" | "deletedAt">,
  selfUserId: string,
  peerLastReadAt: string | null | undefined,
): boolean {
  if (message.deletedAt) return false;
  if (message.senderId !== selfUserId) return false;
  if (!peerLastReadAt) return false;
  return new Date(message.createdAt).getTime() <= new Date(peerLastReadAt).getTime();
}
